import React, { useEffect, useMemo, useCallback } from 'react';
import { Check, X, HelpCircle } from 'lucide-react';
import DualRangeSlider from '../DualRangeSlider';
import { CURRENCY_CONFIGS } from '../../utils/currencyConfigs';
import questionnaireService from '../../services/questionnaireService';

const QuestionInput = ({
    question,
    value,
    onChange,
    saving,
    disabled,
    organization
}) => {
    // Memoize the logging effect
    useEffect(() => {
        if (process.env.NODE_ENV === 'development') {
            console.log('QuestionInput Mounted/Updated:', {
                questionType: question?.question_type,
                value,
                choices: question?.options?.choices,
                saving,
                disabled
            });
        }
    }, [question?.question_type, saving, disabled]); // Remove value from dependencies

    // Memoize value normalization
    const normalizeValue = useCallback((val) => {
        if (!val) return [];
        if (Array.isArray(val)) {
            return val.flat();
        }
        if (typeof val === 'string') {
            try {
                const parsed = JSON.parse(val);
                return Array.isArray(parsed) ? parsed.flat() : [parsed];
            } catch {
                return [val];
            }
        }
        return [val];
    }, []);

    // Memoize choices parsing
    const getChoices = useCallback(() => {
        if (!question.options?.choices) return [];
        try {
            const choices = Array.isArray(question.options.choices)
                ? question.options.choices
                : JSON.parse(question.options.choices);
            console.log('Parsed choices:', choices);
            return choices;
        } catch (e) {
            console.error('Error parsing choices:', e);
            return [];
        }
    }, [question.options?.choices]);

    // Memoize handlers
    const handleCheckboxChange = useCallback((choice) => {
        console.log('handleCheckboxChange called with:', choice);
        const currentValues = normalizeValue(value);
        console.log('Current values:', currentValues);
        const isSelected = currentValues.includes(choice);

        const newValue = isSelected
            ? currentValues.filter(v => v !== choice)
            : [...currentValues, choice];

        onChange(newValue);
    }, [value, onChange, normalizeValue]);

    const handleBudgetChange = useCallback((newValue) => {
        const currentConfig = CURRENCY_CONFIGS[newValue.currencyCode];
        const responseValue = {
            min: parseInt(newValue.min),
            max: parseInt(newValue.max),
            currency: newValue.currency || '$',
            currencyCode: newValue.currencyCode || 'USD',
            usdMin: Math.round(parseInt(newValue.min) / currentConfig.scale),
            usdMax: Math.round(parseInt(newValue.max) / currentConfig.scale)
        };

        // Ensure we're sending a stringified version to the API
        onChange(JSON.stringify(responseValue));
    }, [onChange]);

    // Memoize the choices array
    const choices = useMemo(() => getChoices(), [getChoices]);

    // Memoize the selected values
    const selectedValues = useMemo(() =>
        normalizeValue(value),
        [value, normalizeValue]
    );

    console.log('Rendering multiple choice:', {
        choices,
        selectedValues,
        value,
        rawValue: value,
        valueType: typeof value,
        isArray: Array.isArray(value)
    });

    switch (question.question_type) {
        case 'multiple_choice': {
            return (
                <div className="space-y-4" data-testid="multiple-choice-container">
                    {choices.map((choice, idx) => {
                        const isSelected = selectedValues.includes(choice);

                        return (
                            <label
                                key={`${idx}-${choice}`}
                                data-testid={`choice-${choice}`}
                                className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-all ${isSelected
                                        ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                                        : 'border-gray-200 hover:bg-gray-50'
                                    }`}
                            >
                                <div
                                    className={`relative w-5 h-5 flex items-center justify-center rounded border transition-colors ${isSelected
                                            ? 'bg-blue-500 border-blue-500'
                                            : 'bg-white border-gray-300'
                                        }`}
                                >
                                    {isSelected && (
                                        <Check
                                            className="w-3 h-3 text-white absolute"
                                            data-testid={`check-${choice}`}
                                        />
                                    )}
                                </div>
                                <input
                                    type="checkbox"
                                    className="sr-only"
                                    checked={isSelected}
                                    disabled={saving || disabled}
                                    onChange={() => handleCheckboxChange(choice)}
                                    data-testid={`checkbox-${choice}`}
                                />
                                <span className={`text-sm font-medium ${isSelected ? 'text-blue-700' : 'text-gray-700'
                                    }`}>
                                    {choice}
                                </span>
                            </label>
                        );
                    })}
                </div>
            );
        }

        case 'boolean':
            return (
                <div className="flex gap-4">
                    <button
                        onClick={() => onChange(true)}
                        disabled={saving || disabled}
                        className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md ${value === true
                                ? 'bg-green-600 text-white'
                                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                    >
                        <Check className="w-4 h-4" />
                        Yes
                    </button>
                    <button
                        onClick={() => onChange(false)}
                        disabled={saving || disabled}
                        className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md ${value === false
                                ? 'bg-red-600 text-white'
                                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                    >
                        <X className="w-4 h-4" />
                        No
                    </button>
                </div>
            );

        case 'tristate_boolean':
            return (
                <div className="flex gap-4">
                    <button
                        onClick={() => onChange('YES')}
                        disabled={saving || disabled}
                        className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md ${value === 'YES'
                                ? 'bg-green-600 text-white'
                                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                    >
                        <Check className="w-4 h-4" />
                        Yes
                    </button>
                    <button
                        onClick={() => onChange('NO')}
                        disabled={saving || disabled}
                        className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md ${value === 'NO'
                                ? 'bg-red-600 text-white'
                                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                    >
                        <X className="w-4 h-4" />
                        No
                    </button>
                    <button
                        onClick={() => onChange('DONT_KNOW')}
                        disabled={saving || disabled}
                        className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md ${value === 'DONT_KNOW'
                                ? 'bg-yellow-500 text-white'
                                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                    >
                        <HelpCircle className="w-4 h-4" />
                        Don't Know
                    </button>
                </div>
            );

        // In QuestionInput.jsx, update the range case
        // In QuestionInput.jsx - update the range case
        case 'range': {
            const sliderValue = value ?
                (typeof value === 'string' ? JSON.parse(value) : value)
                : null;

            return (
                <div className="space-y-4">
                    <DualRangeSlider
                        value={sliderValue}
                        onChange={(rangeValue) => {
                            // Ensure we're sending a stringified version to the API
                            onChange(JSON.stringify(rangeValue));
                        }}
                        disabled={saving || disabled}
                    />

                    {/* Helper text for range values */}
                    <div className="text-sm text-gray-500">
                        Please review and confirm the range values before continuing.
                        {!sliderValue && 'Default values will be used if left unchanged.'}
                    </div>

                    {/* Explicit confirmation button */}
                    {!value && (
                        <button
                            onClick={() => {
                                // If there's a default value, explicitly confirm it
                                if (sliderValue) {
                                    onChange(JSON.stringify(sliderValue));
                                }
                            }}
                            className="mt-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100"
                        >
                            Confirm Values
                        </button>
                    )}
                </div>
            );
        }

        default:
            return null;
    }
};

export default React.memo(QuestionInput);